const normalizeLabel = (value) => String(value || "").trim().toLowerCase();
const normalizeId = (value) => String(value || "").trim();
const brokerVisibleSystemTagsById = new Map();
// Reverse index: system-tag key (e.g. "missing_info" / "kyc_failed") -> tag_id, so
// the applications filter and dashboard deep-links can resolve a tag_id to send as
// the backend `tag_ids` filter (which matches on application.tags.tag_id).
const brokerVisibleSystemTagIdsByKey = new Map();
const VISIBLE_SYSTEM_TAG_KINDS = new Set(["application", "applicationform", "application-form", "application_form"]);

export const BROKER_VISIBLE_SYSTEM_TAGS = {
  "missing info": { key: "missing_info", label: "Missing Info", color: "#FF3932" },
  "kyc failed": { key: "kyc_failed", label: "KYC Failed", color: "#780905" },
  "kyc rejected": { key: "kyc_failed", label: "KYC Failed", color: "#780905" },
};

// Single source for the system-tag filter dropdown (and dashboard deep-links),
// derived from the visible-tag registry — add a broker-visible system tag above and
// it appears in the filter automatically. Value is the uppercased key (e.g.
// "MISSING_INFO"), which getBrokerSystemTagId resolves back to a tag_id.
export const BROKER_SYSTEM_TAG_FILTER_OPTIONS = (() => {
  const seen = new Set();
  const options = [];
  Object.values(BROKER_VISIBLE_SYSTEM_TAGS).forEach((tag) => {
    if (tag?.key && !seen.has(tag.key)) {
      seen.add(tag.key);
      options.push({ value: tag.key.toUpperCase(), label: tag.label });
    }
  });
  return options;
})();

const getTagRecordId = (tag = {}) => (
  typeof tag === "string" || typeof tag === "number"
    ? normalizeId(tag)
    : normalizeId(tag?.tag_id || tag?._id || tag?.id)
);

export const getTagDisplayLabel = (tag = {}) => (
  typeof tag === "string" || typeof tag === "number"
    ? String(tag)
    : tag.details || tag.label || tag.name || tag.tag || tag.tag_id || ""
);

export const recordBrokerVisibleSystemTagElements = (records = []) => {
  (Array.isArray(records) ? records : []).forEach((record) => {
    const match = BROKER_VISIBLE_SYSTEM_TAGS[normalizeLabel(record?.label || record?.details)];
    const id = normalizeId(record?._id || record?.id);
    const kind = normalizeLabel(record?.kind);
    const systemGenerated = record?.systemGenerated === true || record?.systemGenerated === "true";
    if (!id || !match || !systemGenerated) return;
    if (kind && !VISIBLE_SYSTEM_TAG_KINDS.has(kind)) return;
    brokerVisibleSystemTagsById.set(id, {
      ...match,
      label: match.label,
      color: record.color || match.color,
    });
    if (match.key && !brokerVisibleSystemTagIdsByKey.has(match.key)) {
      brokerVisibleSystemTagIdsByKey.set(match.key, id);
    }
  });
};

// Resolve a system-tag key ("missing_info" / "kyc_failed", case-insensitive) to its
// tag_id. Returns "" until the system-tag elements have been recorded.
export const getBrokerSystemTagId = (key) => brokerVisibleSystemTagIdsByKey.get(normalizeLabel(key)) || "";

const resolveSystemTag = (tag = {}) => {
  const directMatch = BROKER_VISIBLE_SYSTEM_TAGS[normalizeLabel(getTagDisplayLabel(tag))];
  if (directMatch) return directMatch;
  return brokerVisibleSystemTagsById.get(getTagRecordId(tag));
};

export const getBrokerVisibleSystemTags = (tags) => {
  const seen = new Set();
  return (Array.isArray(tags) ? tags : [])
    .map((tag) => {
      const match = resolveSystemTag(tag);
      if (!match || seen.has(match.key)) return null;
      seen.add(match.key);
      const tagObject = tag && typeof tag === "object" ? tag : {};
      return {
        ...tagObject,
        tag_id: tagObject.tag_id || getTagRecordId(tag) || match.key,
        details: match.label,
        color: match.color,
        systemGenerated: true,
      };
    })
    .filter(Boolean);
};
